/**
 * Story 113 (S97-S106, C1/C2/C3) — the preparation character of a JOB node.
 *
 * Sibling of `jobExternalCharacter.js`, and deliberately a sibling rather than an addition to it:
 * they are two independent declarations of the configurator that happen to sit next to each other in
 * the panel, and folding them together is how the DIVERGENCE below would get lost.
 *
 * Whether a job manages preparation is a property OF THE JOB. A thousand JOB nodes may share a
 * technology and each one manages preparation, or not, independently:
 *
 *     managesPreparation(job) <=> job.managePreparation = TRUE
 *
 * S106/POL5: this module is the SINGLE owner of the predicate, the seed and the gating in Jupiter.
 * The checkbox, and anything else that needs the answer, reads it from here. Copying the formula into
 * a component is what `__tests__/jobPreparationCharacterSingleSource.test.js` forbids.
 */

/**
 * Why the checkbox is not actionable. One reason per factor, so the tooltip names the actual cause
 * instead of a generic message (S104/S105). The values are i18n keys and already exist in the 8
 * locale files -- they are the very same reasons the Extern checkbox uses, so this story adds none.
 */
export const PREPARATION_BLOCK_REASON = {
  NOT_EDITING: 'The workflow is not being edited',
  FABRICATION_ORDER_CLOSED: 'The fabrication order is closed'
}

/**
 * S99/S100 — THE SEED, NOT THE PREDICATE. Its ONLY legitimate caller is the factory that builds a JOB
 * node the moment it is dropped on the canvas (`newJobReference.js`), to decide its initial value.
 *
 * C2: the value travels flat on the palette object (`technologiesWorkflow` -> TechnologyBasicData).
 * An absent, null or unreadable value reads as FALSE, the fail-safe direction: a new node is born
 * unmanaged, exactly as every node is today (S100).
 *
 * D3/S101: nothing recalculates it afterwards. Changing the technology of an existing node leaves the
 * mark exactly as the configurator left it, in either direction -- it is a declaration, not derived
 * data, and calling this from a component would undo that.
 *
 * @param {{managePreparation?: boolean}} [technology]
 * @returns {boolean}
 */
export const isTechnologyManagingPreparation = (technology) => {
  return technology?.managePreparation === true
}

/**
 * The predicate: the configurator's mark on that job, and nothing else.
 *
 * A missing mark reads as false, exactly as every reader in Sun treats the nullable column (RN61).
 * Note what this is NOT: a function of the technology (POL5 forbids consulting it at runtime).
 *
 * @param {{managePreparation?: boolean}} [jobReference]
 * @returns {boolean}
 */
export const managesPreparation = (jobReference) => {
  return jobReference?.managePreparation === true
}

/**
 * S103/S104/S105 (D4, RN12.1) — the gating, and its DELIBERATE divergence from the twin checkbox.
 *
 * `Extern` is gated by `editing AND NOT hasParts AND ofOperable`. This one is gated by
 * `editing AND ofOperable` ONLY: **there is no `hasParts` condition**. D4 was signed that way because
 * the two marks mean different things in time -- carrying a job through the external circuit once it
 * has produced parts would rewrite its past, while deciding to start measuring preparation from now
 * on is a forward-looking change that a running job is entitled to make (and E7/S132 already covers
 * the operator's side of turning it off mid-job).
 *
 * Inheriting `!hasParts` "for symmetry" is therefore the mistake this javadoc exists to prevent, and
 * S103 is the test that catches it: it asserts the two checkboxes disagree on a job WITH parts.
 *
 * Degradation follows the editor's own convention: `fabricationOrderOperable` absent
 * (template/subworkflow, where Sun does not compute it) reads as operable and does NOT block, so in a
 * template the checkbox stays editable.
 *
 * @param {Object} params
 * @param {boolean} [params.editing]
 * @param {boolean} [params.fabricationOrderOperable]
 * @returns {{actionable: boolean, blockReason: string}}
 */
export const resolvePreparationCheckboxGating = ({ editing, fabricationOrderOperable = true } = {}) => {
  if (editing !== true) {
    return { actionable: false, blockReason: PREPARATION_BLOCK_REASON.NOT_EDITING }
  }
  if (fabricationOrderOperable !== true) {
    return { actionable: false, blockReason: PREPARATION_BLOCK_REASON.FABRICATION_ORDER_CLOSED }
  }
  return { actionable: true, blockReason: '' }
}
